/* global globalThis */
import React, { useCallback, useMemo, useState } from "react";

import {
  confirmOpcuaServerRoleConfig,
  getOpcuaServerRoleConfig,
  getOpcuaServerRoleEditConfig,
  getOpcuaServerRoleStatus,
  previewOpcuaServerRoleConfig,
} from "../../../api";
import chevronIcon from "../../../assets/chevron-right.svg";
import NumberInput from "../../../components/NumberInput";
import Select from "../../../components/Select";
import TagPicker from "../../../components/TagPicker";
import { GatewayStatusPill } from "../../../components/GatewayStatus";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import xmarkIcon from "../../../assets/xmark.svg";
import {
  assertOpcuaServerConfirmation,
  assertOpcuaServerEditConfig,
  assertOpcuaServerPreview,
  buildOpcuaServerPayload,
  createOpcuaServerMapping,
  normalizeOpcuaServerStatus,
  opcuaServerPreviewExpired,
  opcuaServerFormFromConfig,
  validateOpcuaServerForm,
} from "./plc-opcua-server-model";

const FieldError = ({ children }) =>
  children ? <div className="plcProtocolFieldError">{children}</div> : null;

const NumberField = ({ label, hint, error, disabled, ...props }) => (
  <div className="gwField">
    <label className="gwLabel">{label}</label>
    <NumberInput {...props} disabled={disabled} aria-invalid={!!error} />
    {hint ? <div className="gwHint">{hint}</div> : null}
    <FieldError>{error}</FieldError>
  </div>
);

const textError = (error, fallback) =>
  String(error?.message || error || "").trim() || fallback;

const secureMutationKey = () => {
  const cryptoAPI = typeof window !== "undefined" ? window.crypto : globalThis.crypto;
  if (typeof cryptoAPI?.randomUUID === "function") return cryptoAPI.randomUUID();
  if (typeof cryptoAPI?.getRandomValues !== "function") {
    throw new Error("Secure browser randomness is unavailable for OPC UA configuration review.");
  }
  const bytes = cryptoAPI.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const semanticValue = (value) => {
  if (value == null || value === "") return "Not configured";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" || typeof value === "number") return String(value);
  try { return JSON.stringify(value); } catch { return "Changed"; }
};

const opcuaMappingChange = (change) => String(change?.field || "")
  .replaceAll("[", ".")
  .replaceAll("]", ".")
  .split(/[./]/)
  .some((segment) => /^mappings?$/i.test(segment));
const missingMappingValue = (value) => /^(|none|absent|not present|not configured)$/i.test(String(value || "").trim());
const mappingOperation = (change) => {
  if (!opcuaMappingChange(change)) return null;
  const before = String(change.before || "");
  const after = String(change.after || "");
  if (!/read[_ /-]?write/i.test(before) && /read[_ /-]?write/i.test(after)) {
    return { label: "Write access added", tone: "removed" };
  }
  if (missingMappingValue(before) && !missingMappingValue(after)) return { label: "Mapping added", tone: "added" };
  if (!missingMappingValue(before) && missingMappingValue(after)) return { label: "Mapping removed", tone: "removed" };
  return { label: "Mapping changed", tone: "changed" };
};

const securityReferenceValue = (form, referenceField, configuredField, editable) => (
  editable ? form[referenceField] : (form[configuredField] ? "Configured" : "Not configured")
);

const SecurityReferenceField = ({ label, form, referenceField, configuredField, canConfigure, editable, placeholder, onChange }) => {
  const configured = form[configuredField] === true || !!form[referenceField];
  return (
    <div
      className="gwField"
      data-gw-ai-sensitive-field="configured-only"
      data-gw-ai-configured={configured ? "true" : "false"}
    >
      <label className="gwLabel">{label}</label>
      <input
        className="gwInput"
        value={securityReferenceValue(form, referenceField, configuredField, canConfigure)}
        onChange={onChange(referenceField)}
        disabled={!editable}
        placeholder={placeholder}
        autoComplete="off"
      />
    </div>
  );
};

const AI_PUBLIC_CONFIG_FIELDS = Object.freeze([
  "listen_address",
  "namespace_uri",
  "source_cidrs",
  "identity_mode",
  "identity_provider",
  "max_channels",
  "max_sessions",
  "max_subscriptions_per_session",
  "max_monitored_items_per_subscription",
  "max_nodes_per_read",
  "max_nodes_per_write",
]);

const OpcuaReviewModal = ({ proposal, candidate, reason, saving, onCancel, onConfirm }) => {
  const [clock, setClock] = useState(() => Date.now());
  const expired = opcuaServerPreviewExpired(proposal, clock);
  const mappingChanges = proposal.semantic_diff.filter(opcuaMappingChange);
  const writeEscalation = mappingChanges.some((change) => mappingOperation(change)?.label === "Write access added");
  React.useEffect(() => {
    const expiresAt = Date.parse(proposal?.expires_at);
    const remaining = expiresAt - Date.now();
    if (!Number.isFinite(expiresAt) || remaining <= 0) return undefined;
    const timer = window.setTimeout(() => setClock(Date.now()), Math.min(remaining + 25, 60000));
    return () => window.clearTimeout(timer);
  }, [proposal, clock]);
  return (
    <div className="gwOverlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !saving) onCancel();
    }}>
      <div
        className="gwModal plcS7ReviewModal"
        role="dialog"
        aria-modal="true"
        aria-label="Review OPC UA namespace revision"
        data-gw-ai-form-control="reviewed-opcua"
        data-gw-ai-protected-submit="reviewed-opcua-confirm"
      >
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Review OPC UA namespace revision</div>
            <div className="gwModalSubtitle">Inspect the exact disabled desired revision, then confirm it separately.</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onCancel} disabled={saving} aria-label="Close OPC UA review">
            <img src={xmarkIcon} alt="" className="gwIcon" />
          </button>
        </div>
        <div className="gwModalBody plcS7ReviewBody">
          <div className="plcProtocolRuntimeSummary">
            <div><span>Desired action</span><strong>Save disabled revision</strong></div>
            <div><span>Endpoint draft</span><strong>{candidate.listen_address}</strong></div>
            <div><span>Current base</span><strong>{proposal.base_revision ? `Revision ${proposal.base_revision}` : "Legacy draft"}</strong></div>
            <div><span>Mappings</span><strong>{candidate.mappings.length}</strong></div>
          </div>
          <div className={expired ? "plcProtocolRuntimeError" : "gwNotice isSoft"}>
            <strong>{expired ? "Review expired" : "Immutable review digest"}</strong>
            {expired ? <span>Create a fresh preview before confirming.</span> : <code className="plcS7Digest">{proposal.proposal_sha256}</code>}
          </div>
          <div className="gwNotice isSoft"><strong>Reason</strong><span>{reason}</span></div>
          {mappingChanges.length ? (
            <div className="plcProtocolExposureWarning" role="note">
              <strong>{writeEscalation ? "External write access changes require extra attention" : "Namespace mapping changes"}</strong>
              <span>{writeEscalation
                ? "At least one mapped node gains read/write access. An accepted OPC UA command must still pass normal tag ownership and does not prove observed or physical state."
                : `${mappingChanges.length} mapping change${mappingChanges.length === 1 ? "" : "s"} will alter the public namespace. Review every public NodeId, tag path, and access mode.`}</span>
            </div>
          ) : null}
          <div className="plcS7SemanticDiff">
            <div className="plcS7DiffHeader">
              <strong>Semantic diff</strong>
              <span>{proposal.semantic_diff.length} change{proposal.semantic_diff.length === 1 ? "" : "s"}</span>
            </div>
            {proposal.semantic_diff.length ? proposal.semantic_diff.map((change, index) => (
              <div className="plcS7DiffRow" key={`${change.field}-${index}`}>
                <div className="plcS7DiffIdentity">
                  {mappingOperation(change) ? <span className={`plcS7DiffOperation is-${mappingOperation(change).tone}`}>{mappingOperation(change).label}</span> : null}
                  <code>{change.field}</code>
                </div>
                <div className="plcS7DiffValues">
                  <div><span>Before</span><pre>{semanticValue(change.before)}</pre></div>
                  <div><span>After</span><pre>{semanticValue(change.after)}</pre></div>
                </div>
              </div>
            )) : <div className="gwEmptyState gwEmptyCompact"><div className="gwEmptyBody">The server reports no semantic change.</div></div>}
          </div>
          {(proposal.warnings || []).length ? <ul className="plcOpcuaWarningList">{proposal.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul> : null}
          <div className="plcProtocolRuntimeNotice" role="note">
            <strong>Desired is not applied</strong>
            <span>This configuration-only build saves a reviewed desired revision but has no OPC UA listener runtime. Applied and listening remain independently unavailable.</span>
          </div>
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="gwPrimaryBtn" type="button" onClick={onConfirm} disabled={saving || expired} data-gw-ai-reviewed-action="confirm">
            {saving ? "Confirming…" : "Confirm desired revision"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Operators see a summary, not the backend's snake_case blocker identifier;
// the raw code stays available in the tooltip for support conversations.
const BLOCKER_LABELS = {
  approved_stack_security_contract_missing: "Server stack security contract incomplete",
};

const blockerLabel = (code) => {
  if (!code) return "Security contract";
  if (BLOCKER_LABELS[code]) return BLOCKER_LABELS[code];
  const cleaned = String(code).replace(/_/g, " ").trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const MappingEditor = ({ mapping, index, error, disabled, onChange, onRemove }) => {
  const set = (field) => (event) => onChange(index, { [field]: event.target.value });
  return (
    <div
      className="plcProtocolMapping"
      data-gw-resource="opcua_server_mappings"
      data-gw-ai-protected-field-group="opcua-mapping"
    >
      <div className="plcProtocolMappingHeader">
        <div>
          <strong>Node {index + 1}</strong>
          <span className={`plcProtocolAccess ${mapping.access === "read_write" ? "isWritable" : ""}`}>
            {mapping.access === "read_write" ? "Read / write" : "Read only"}
          </span>
        </div>
        <button
          className="gwIconButton"
          type="button"
          disabled={disabled}
          onClick={() => onRemove(index)}
          aria-label={`Remove OPC UA node ${index + 1}`}
          title="Remove node"
        >
          <img src={xmarkIcon} alt="" className="gwIcon" />
        </button>
      </div>
      <div className="plcOpcuaMappingGrid">
        <div className="gwField">
          <label className="gwLabel">Stable node identifier</label>
          <input
            className="gwInput"
            value={mapping.nodeId}
            onChange={set("nodeId")}
            placeholder="Line1/Motor/Speed"
            disabled={disabled}
            aria-invalid={!!error}
          />
          <div className="gwHint">Public string NodeId inside the configured namespace; internal tag IDs are never exposed.</div>
        </div>
        <div className="gwField plcProtocolTagField">
          <label className="gwLabel">Control Seat tag path</label>
          <TagPicker
            value=""
            displayValue={mapping.tagPath}
            placeholder="Search or choose a tag…"
            disabled={disabled}
            onChange={(_tagID, path) => onChange(index, { tagPath: path })}
          />
          <div className="gwHint">Normal unified tag full path; renames remain stable through the private mapping identity.</div>
        </div>
        <div className="gwField">
          <label className="gwLabel">Access</label>
          <Select
            value={mapping.access}
            options={[{ value: "read", label: "Read only" }, { value: "read_write", label: "Read / write" }]}
            onChange={(value) => onChange(index, { access: value })}
            disabled={disabled}
          />
          <div className="gwHint">Writes pass the unified ownership-aware command path.</div>
        </div>
        <div className="gwField">
          <label className="gwLabel">Resolved scalar type</label>
          <input className="gwInput" value={mapping.dataType || "Resolved on save"} disabled readOnly />
        </div>
      </div>
      <FieldError>{error}</FieldError>
    </div>
  );
};

export const OpcuaServerConfiguration = ({ active = true, availabilityOnly = false }) => {
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");
  const [review, setReview] = useState(null);
  const [loadError, setLoadError] = useState("");
	const loadedRef = React.useRef(false);
  const notify = useNotify();
  const canConfigure = usePermission("configure");

  const payload = useMemo(() => form ? buildOpcuaServerPayload(form) : null, [form]);
  const fingerprint = useMemo(() => payload ? JSON.stringify(payload) : "", [payload]);
  const dirty = !!baseline && fingerprint !== baseline;
  const validation = useMemo(
    () => form ? validateOpcuaServerForm(form) : { valid: false, message: "Configuration has not loaded.", formErrors: {}, mappingErrors: [] },
    [form]
  );
  const editable = !!form && canConfigure && !saving;

  const load = useCallback(async ({ preserveForm = false } = {}) => {
    setLoading(true);
    setLoadError("");
    if (availabilityOnly) {
      try {
        setStatus(normalizeOpcuaServerStatus(await getOpcuaServerRoleStatus()));
      } catch (error) {
        setLoadError(textError(error, "Could not load OPC UA server availability."));
      } finally {
        setLoading(false);
      }
      return;
    }
    const [configResult, statusResult] = await Promise.allSettled([
      canConfigure ? getOpcuaServerRoleEditConfig() : getOpcuaServerRoleConfig(),
      getOpcuaServerRoleStatus(),
    ]);
    if (statusResult.status === "fulfilled") {
      setStatus(normalizeOpcuaServerStatus(statusResult.value));
    }
    if (configResult.status === "fulfilled") {
      const config = canConfigure
        ? assertOpcuaServerEditConfig(configResult.value).config
        : configResult.value;
      const nextForm = opcuaServerFormFromConfig(config);
      if (!preserveForm) setForm(nextForm);
      setBaseline(JSON.stringify(buildOpcuaServerPayload(nextForm)));
    } else {
      setLoadError(textError(configResult.reason, "Could not load the OPC UA server-role configuration."));
    }
    setLoading(false);
  }, [availabilityOnly, canConfigure]);

  React.useEffect(() => {
	if (!active) return;
	load({ preserveForm: loadedRef.current }).finally(() => { loadedRef.current = true; });
  }, [active, load]);

  React.useEffect(() => {
    if (availabilityOnly) return undefined;
    const openProposal = async (event) => {
      const detail = event?.detail?.payload || {};
      try {
        if (!canConfigure) throw new Error("Configure permission is required to prepare this OPC UA review.");
        const [editEnvelope, freshStatus] = await Promise.all([
          getOpcuaServerRoleEditConfig(),
          getOpcuaServerRoleStatus(),
        ]);
        const editConfig = assertOpcuaServerEditConfig(editEnvelope).config;
        const revision = Number(editConfig.revision || 0);
        const digest = String(editConfig.digest || "");
        if (Number(freshStatus?.desired_revision || 0) !== revision) {
          throw new Error("The OPC UA desired revision changed. Read it again before proposing a change.");
        }
        const candidate = { ...editConfig };
        AI_PUBLIC_CONFIG_FIELDS.forEach((field) => {
          if (Object.prototype.hasOwnProperty.call(detail.changes || {}, field)) {
            candidate[field] = detail.changes[field];
          }
        });
        delete candidate.revision;
        delete candidate.digest;
        candidate.enabled = false;
        candidate.security_mode = "sign_and_encrypt";
        candidate.security_policy = "aes256_sha256_rsapss";
        candidate.anonymous_enabled = false;
        const requestedReason = String(detail.reason || "").trim();
        const preview = assertOpcuaServerPreview(await previewOpcuaServerRoleConfig({
          config: candidate,
          expected_revision: revision,
          reason: requestedReason,
        }, secureMutationKey()), revision);
        const nextForm = opcuaServerFormFromConfig({ ...candidate, revision, digest });
        setForm(nextForm);
        setBaseline(JSON.stringify(buildOpcuaServerPayload(opcuaServerFormFromConfig(editConfig))));
        setStatus(normalizeOpcuaServerStatus(freshStatus));
        setReason(requestedReason);
        setReview({ proposal: preview.proposal, candidate: buildOpcuaServerPayload(nextForm) });
        document.querySelector('[data-gw-resource="opcua_server_role"]')?.scrollIntoView?.({ behavior: "smooth", block: "start" });
      } catch (error) {
        notify({ type: "error", title: "OPC UA proposal unavailable", message: textError(error, "Create a fresh reviewed proposal.") });
      }
    };
    window.addEventListener("gw-ai-propose-opcua-server-role-page", openProposal);
    return () => window.removeEventListener("gw-ai-propose-opcua-server-role-page", openProposal);
  }, [availabilityOnly, canConfigure, notify]);

  const setField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };
  const changeMapping = (index, patch) => {
    setForm((current) => ({
      ...current,
      mappings: current.mappings.map((mapping, candidate) =>
        candidate === index ? { ...mapping, ...patch } : mapping
      ),
    }));
  };
  const removeMapping = (index) => {
    setForm((current) => ({ ...current, mappings: current.mappings.filter((_, candidate) => candidate !== index) }));
  };
  const addMapping = () => {
    setForm((current) => ({ ...current, mappings: [...current.mappings, createOpcuaServerMapping()] }));
  };
  const preview = async () => {
    if (!validation.valid) {
      notify({ type: "error", title: "OPC UA configuration needs attention", message: validation.message });
      return;
    }
    if (reason.trim().length < 3) {
      notify({ type: "error", title: "Review reason required", message: "Enter at least three characters explaining this desired revision." });
      return;
    }
    setSaving(true);
    try {
      const response = assertOpcuaServerPreview(await previewOpcuaServerRoleConfig({
        config: payload,
        expected_revision: form.revision,
        reason: reason.trim(),
      }, secureMutationKey()), form.revision);
      setReview({ proposal: response.proposal, candidate: payload });
    } catch (error) {
      notify({ type: "error", title: "OPC UA preview failed", message: textError(error, "The server rejected the reviewed configuration.") });
    } finally {
      setSaving(false);
    }
  };

  const confirm = async () => {
    if (!review?.proposal) return;
    setSaving(true);
    try {
      const response = assertOpcuaServerConfirmation(await confirmOpcuaServerRoleConfig({
        confirmation_token: review.proposal.confirmation_token,
        proposal_sha256: review.proposal.proposal_sha256,
      }, secureMutationKey()));
      setReview(null);
      setReason("");
      setStatus(normalizeOpcuaServerStatus(response.status));
      const edit = assertOpcuaServerEditConfig(await getOpcuaServerRoleEditConfig());
      const nextForm = opcuaServerFormFromConfig(edit.config);
      setForm(nextForm);
      setBaseline(JSON.stringify(buildOpcuaServerPayload(nextForm)));
      notify({
        type: "success",
        title: "OPC UA desired revision saved",
        message: `Desired revision ${response.config.revision} is durable. Applied remains unavailable and the listener stays closed.`,
      });
    } catch (error) {
      notify({ type: "error", title: "OPC UA confirmation failed", message: textError(error, "The review expired or the desired base changed.") });
    } finally {
      setSaving(false);
    }
  };

  if (availabilityOnly) {
    return (
      <section className="gwFormStack plcOpcuaServer" data-gw-resource="opcua_server_role">
        <div className="gwCardSection plcServerOverviewCard">
          <div className="gwSectionTitle plcProtocolSectionTitle">
            <span>Availability</span>
            <GatewayStatusPill className="plcProtocolStatus is-unavailable" indicator>
              {loading ? "Checking…" : "Not available yet"}
            </GatewayStatusPill>
          </div>
          <div className="gwCardSectionBody plcServerOverviewBody">
            <div className="plcServerLead" role="note">
              <strong>External OPC UA clients cannot connect to Control Seat yet</strong>
              <span>The inbound OPC UA server will appear here when it is ready to use.</span>
            </div>
            <div className="gwNotice isSoft">
              To connect Control Seat to an existing OPC UA server, use Devices &gt; OPC UA.
            </div>
            {loadError ? (
              <div className="gwNotice isSoft" role="status">
                Server availability could not be checked. Refresh the page to try again.
              </div>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="gwFormStack plcOpcuaServer"
      data-gw-resource="opcua_server_role"
      data-gw-ai-form-control={availabilityOnly ? undefined : "reviewed-opcua"}
    >
      {/* Subordinate heading — this section shares the Protocol Servers page,
          which already owns the single page title. */}
      <div className="gwPageHeader plcOpcuaHeader">
        <div>
          <div className="plcOpcuaTitle">OPC UA server</div>
          <div className="gwPageSubtitle">
            {availabilityOnly
              ? "Inbound server availability. OPC UA client connections are managed under Devices."
              : "Prepare a secure Control Seat namespace for selected unified tags. This is the inbound server role, not the OPC UA client under Devices."}
          </div>
        </div>
        <button className="gwSecondaryBtn" type="button" disabled={loading} onClick={() => load({ preserveForm: dirty })}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {loading && !form ? <div className="gwHint">Loading OPC UA server state…</div> : null}

      {loadError ? (
        <div className="gwAlert" role="alert">
          <div className="gwAlertTitle">OPC UA server configuration unavailable</div>
          <div className="gwAlertBody">{loadError}</div>
        </div>
      ) : null}

      {status ? <>
        <div className="gwCardSection plcServerOverviewCard">
          <div className="gwSectionTitle plcProtocolSectionTitle">
            <span>Availability</span>
            <GatewayStatusPill className="plcProtocolStatus is-unavailable" indicator>Configuration only</GatewayStatusPill>
          </div>
          <div className="gwCardSectionBody plcServerOverviewBody">
            <div className="plcServerLead" role="note">
              <strong>Configuration only — activation is blocked</strong>
              <span>{status.reason || "The approved OPC UA dependency cannot yet enforce authenticated identities, bounded sessions/subscriptions, and ownership-aware writes."}</span>
            </div>
            <div className="plcServerFactGrid">
              <div><span>Listener</span><strong>Closed</strong></div>
              <div><span>Desired</span><strong>{status.desiredRevision ? `Revision ${status.desiredRevision} · Disabled` : "Legacy draft · Disabled"}</strong></div>
              <div><span>Applied</span><strong>{status.appliedRevision ? `Revision ${status.appliedRevision}` : "Unavailable"}</strong></div>
              <div><span>Mappings</span><strong>{status.mappingCount}</strong></div>
            </div>
            <div className="plcServerContextNote">
              No listener or writable namespace can be configured until an executable server stack is available.
            </div>
          </div>
        </div>

        <details className="gwCardSection plcServerDetails">
          <summary className="gwSectionTitle plcProtocolSectionTitle">
            <div><span>Technical details</span><small>Saved intent, blockers, and implementation evidence</small></div>
            <img src={chevronIcon} alt="" className="plcServerDisclosureChevron" aria-hidden="true" />
          </summary>
          <div className="gwCardSectionBody plcServerDetailsBody">
            <div className="plcServerTechnicalRows">
              <div><span>Endpoint draft</span><strong>{status.listenAddress || "Not configured"}</strong></div>
              <div><span>Blocked by</span><strong title={status.blockerCode || undefined}>{blockerLabel(status.blockerCode)}</strong></div>
              <div><span>Reconciliation</span><strong>{status.reconciled ? "Reconciled" : "Not reconciled"}</strong></div>
            </div>
            {!status.reconciled ? (
              <div className="plcServerDetailSection" role="note">
                <strong>Desired is not applied</strong>
                <span>Desired revision {status.desiredRevision || 0} is durable configuration intent only. It is not applied runtime state and does not prove an OPC UA endpoint is listening.</span>
              </div>
            ) : null}
            {status.readProjection?.available ? (
              <div className="plcServerDetailSection" role="note">
                <strong>Bounded read foundation · {status.readProjection.profile}</strong>
                <span>
                  {status.readProjection.scalarTypes.join(" · ")} · requests up to {status.readProjection.maxRequestBytes.toLocaleString()} bytes · responses up to {status.readProjection.maxResponseBytes.toLocaleString()} bytes
                </span>
                <span>
                  Internal codec/projection only. Network listener {status.readProjection.networkListenerOwned ? "owned" : "not present"}; writes {status.readProjection.writesEnabled ? "enabled" : "always rejected"}. This is not OPC UA browse, read, subscription, or write service availability.
                </span>
              </div>
            ) : null}
            {status.writeStateContract ? (
              <div className="plcServerDetailSection">
                <strong>Write behavior</strong>
                <span>{status.writeStateContract}</span>
              </div>
            ) : null}
            {(status.warnings || []).length ? (
              <div className="plcServerDetailSection" role="note">
                <strong>Warnings</strong>
                <ul className="plcOpcuaWarningList">
                  {status.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                </ul>
              </div>
            ) : null}
            {(status.dependencies || []).map((dependency) => (
              <div className="plcOpcuaDependency" key={`${dependency.module}@${dependency.version}`}>
                <strong>{dependency.module} {dependency.version}</strong>
                <span>{dependency.license} · {dependency.usage}</span>
              </div>
            ))}
          </div>
        </details>
      </> : null}

      {form && !availabilityOnly ? (
        <>
          <div className="gwCardSection">
            <div className="gwSectionTitle plcProtocolSectionTitle">
              <span>Secure endpoint draft</span>
              {dirty ? <span className="plcProtocolUnsaved">Unsaved changes</span> : null}
            </div>
            <div className="gwCardSectionBody">
              <div className="gwToggleRow plcProtocolEnableRow">
                <div>
                  <div className="gwLabel">Enable OPC UA server</div>
                  <div className="gwHint">Unavailable until the server stack passes authentication, write-path, resource-limit, and interoperability gates.</div>
                </div>
                <button type="button" className="gwToggle isDisabled" role="switch" aria-checked="false" disabled aria-label="OPC UA server activation blocked"><span /></button>
              </div>
              <div className="plcOpcuaConfigGrid">
                <div className="gwField plcProtocolListenHost">
                  <label className="gwLabel">Listen IP address</label>
                  <input className="gwInput" value={form.listenHost} onChange={setField("listenHost")} disabled={!editable} aria-invalid={!!validation.formErrors.listenHost} />
                  <div className="gwHint">One explicit interface only; wildcard listeners are rejected.</div>
                  <FieldError>{validation.formErrors.listenHost}</FieldError>
                </div>
                <NumberField label="TCP port" value={form.listenPort} min={1} max={65535} step={1} onChange={setField("listenPort")} disabled={!editable} error={validation.formErrors.listenPort} />
                <div className="gwField plcOpcuaNamespace">
                  <label className="gwLabel">Namespace URI</label>
                  <input className="gwInput" value={form.namespaceUri} onChange={setField("namespaceUri")} disabled={!editable} aria-invalid={!!validation.formErrors.namespaceUri} />
                  <FieldError>{validation.formErrors.namespaceUri}</FieldError>
                </div>
                <div className="gwField">
                  <label className="gwLabel">Security</label>
                  <input className="gwInput" value="Sign and encrypt · Aes256_Sha256_RsaPss" disabled readOnly />
                  <div className="gwHint">Anonymous and insecure modes are rejected.</div>
                </div>
                <div className="gwField">
                  <label className="gwLabel">Identity mode</label>
                  <Select
                    value={form.identityMode}
                    options={[
                      { value: "username_or_certificate", label: "Username or certificate" },
                      { value: "certificate_only", label: "Certificate only" },
                    ]}
                    onChange={(value) => setForm((current) => ({ ...current, identityMode: value }))}
                    disabled={!editable}
                  />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Identity provider</label>
                  <input className="gwInput" value={form.identityProvider} onChange={setField("identityProvider")} disabled={!editable} aria-invalid={!!validation.formErrors.identityProvider} />
                  <FieldError>{validation.formErrors.identityProvider}</FieldError>
                </div>
                <div className="gwField plcOpcuaSourceNetworks">
                  <label className="gwLabel">Allowed source addresses and networks</label>
                  <textarea
                    className="gwInput"
                    rows={3}
                    value={form.sourceCidrsText}
                    onChange={setField("sourceCidrsText")}
                    disabled={!editable}
                    placeholder={"10.20.0.0/24\n10.20.0.15"}
                    aria-invalid={!!validation.formErrors.sourceCidrs}
                  />
                  <div className="gwHint">One canonical unicast IP address or CIDR per line. This is network admission, not user identity.</div>
                  <FieldError>{validation.formErrors.sourceCidrs}</FieldError>
                </div>
              </div>
            </div>
          </div>

          <div
            className="gwCardSection"
            data-gw-resource="opcua_server_security_refs"
            data-gw-ai-sensitive-resource="configured-only"
          >
            <div className="gwSectionTitle"><span>Certificates and trust</span></div>
            <div className="gwCardSectionBody">
              <div className="gwNotice isSoft">
                These configure-only reference names are available to this human form and are never included in AI-visible configuration results. Private keys and certificate contents are never returned.
              </div>
              <div className="plcOpcuaSecurityGrid">
                <SecurityReferenceField label="Server certificate reference" form={form} referenceField="serverCertificateRef" configuredField="serverCertificateConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-server-cert" onChange={setField} />
                <SecurityReferenceField label="Server private-key reference" form={form} referenceField="serverPrivateKeyRef" configuredField="serverPrivateKeyConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-server-key" onChange={setField} />
                <SecurityReferenceField label="Application trust-list reference" form={form} referenceField="applicationTrustListRef" configuredField="applicationTrustListConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-app-trust" onChange={setField} />
                <SecurityReferenceField label="Application issuer-list reference" form={form} referenceField="applicationIssuerListRef" configuredField="applicationIssuerListConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-app-issuers" onChange={setField} />
                <SecurityReferenceField label="Application CRL reference" form={form} referenceField="applicationCRLRef" configuredField="applicationCRLConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-app-crl" onChange={setField} />
                <SecurityReferenceField label="X.509 user trust-list reference" form={form} referenceField="x509UserTrustListRef" configuredField="x509UserTrustListConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-user-trust" onChange={setField} />
                <SecurityReferenceField label="X.509 user issuer-list reference" form={form} referenceField="x509UserIssuerListRef" configuredField="x509UserIssuerListConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-user-issuers" onChange={setField} />
                <SecurityReferenceField label="X.509 user CRL reference" form={form} referenceField="x509UserCRLRef" configuredField="x509UserCRLConfigured" canConfigure={canConfigure} editable={editable} placeholder="opcua-user-crl" onChange={setField} />
              </div>
              <FieldError>{validation.formErrors.securityReferences}</FieldError>
            </div>
          </div>

          <div className="gwCardSection">
            <div className="gwSectionTitle"><span>Bounded resources</span></div>
            <div className="gwCardSectionBody plcOpcuaLimitsGrid">
              <NumberField label="Maximum channels" value={form.maxChannels} min={1} max={256} step={1} onChange={setField("maxChannels")} disabled={!editable} error={validation.formErrors.maxChannels} />
              <NumberField label="Maximum sessions" value={form.maxSessions} min={1} max={256} step={1} onChange={setField("maxSessions")} disabled={!editable} error={validation.formErrors.maxSessions} />
              <NumberField label="Subscriptions per session" value={form.maxSubscriptionsPerSession} min={1} max={1024} step={1} onChange={setField("maxSubscriptionsPerSession")} disabled={!editable} error={validation.formErrors.maxSubscriptionsPerSession} />
              <NumberField label="Items per subscription" value={form.maxMonitoredItemsPerSubscription} min={1} max={65536} step={1} onChange={setField("maxMonitoredItemsPerSubscription")} disabled={!editable} error={validation.formErrors.maxMonitoredItemsPerSubscription} />
              <NumberField label="Nodes per read" value={form.maxNodesPerRead} min={1} max={4096} step={1} onChange={setField("maxNodesPerRead")} disabled={!editable} error={validation.formErrors.maxNodesPerRead} />
              <NumberField label="Nodes per write" value={form.maxNodesPerWrite} min={1} max={1024} step={1} onChange={setField("maxNodesPerWrite")} disabled={!editable} error={validation.formErrors.maxNodesPerWrite} />
            </div>
          </div>

          <div className="gwCardSection" data-gw-ai-protected-field-group="opcua-mapping">
            <div className="gwSectionTitle plcProtocolSectionTitle">
              <div><span>Namespace mappings</span><small>{form.mappings.length} node{form.mappings.length === 1 ? "" : "s"}</small></div>
              <button className="gwSecondaryBtn plcProtocolAddButton" type="button" disabled={!editable} onClick={addMapping}>Add node</button>
            </div>
            <div className="gwCardSectionBody">
              <div className="gwNotice isSoft">
                Read/write is only a requested permission. A future accepted write must route through the same tag/PLC ownership command path as SCADA and must not publish an optimistic observed value.
              </div>
              {form.mappings.length ? (
                <div className="plcProtocolMappings">
                  {form.mappings.map((mapping, index) => (
                    <MappingEditor key={mapping.key} mapping={mapping} index={index} error={validation.mappingErrors[index]} disabled={!editable} onChange={changeMapping} onRemove={removeMapping} />
                  ))}
                </div>
              ) : (
                <div className="gwEmptyState gwEmptyCompact">
                  <div className="gwEmptyTitle">No tags exposed</div>
                  <div className="gwEmptyBody">Add a node to prepare the future Control Seat namespace.</div>
                </div>
              )}
              {!canConfigure ? <div className="gwNotice isSoft">Configure permission is required to edit this draft.</div> : null}
              <div className="gwField plcOpcuaReviewReason">
                <label className="gwLabel">Reason for desired revision</label>
                <textarea
                  className="gwInput"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  disabled={!editable}
                  placeholder="Why should this OPC UA namespace draft change?"
                />
                <div className="gwHint">Preview creates a short-lived actor-bound review. It does not apply or open a listener.</div>
              </div>
              <div className="plcProtocolSaveRow">
                <div>
                  <strong>{validation.valid ? "Disabled desired revision is ready for review" : "Configuration needs attention"}</strong>
                  <span>{validation.valid ? "Preview the exact semantic diff, then confirm it separately. Applied remains unavailable." : validation.message}</span>
                </div>
                <button className="gwPrimaryBtn" type="button" disabled={!editable || !dirty || !validation.valid || reason.trim().length < 3} onClick={preview} data-gw-ai-reviewed-action="preview">{saving ? "Preparing…" : "Preview changes"}</button>
              </div>
            </div>
          </div>
        </>
      ) : null}
      {review?.proposal ? (
        <OpcuaReviewModal
          proposal={review.proposal}
          candidate={review.candidate}
          reason={reason.trim()}
          saving={saving}
          onCancel={() => !saving && setReview(null)}
          onConfirm={confirm}
        />
      ) : null}
    </section>
  );
};
